import { useAppDispatch } from '@/hooks';
import { READED } from '@/widgets/Comment/constants';
import { DragEndEvent } from '@dnd-kit/core';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { STATUS_NAME } from '@/pages/project-management/detail/constant';
import { ITask } from '@/pages/project-management/detail/utils';
import { isPending, renderTaskStatus } from '@/pages/project-management/detail/utils/helper';

import EmptyTextData from '@/components/EmptyData/EmptyTextData';
import AppTooltip from '@/components/app-tooltip/AppTooltip';
import { BaseButton } from '@/components/base-button/BaseButton';
import { showBasePopup } from '@/components/base-popup/BasePopup';
import { TagTaskType } from '@/components/common/task/TagTaskType';
import { DragHandle } from '@/components/table/BaseTable';

import { setAlertNotification } from '@/redux/globalReducer';

import useAuthorization from '@/hooks/useAuthorization';
import useMutation from '@/hooks/useMutation';

import { FORMAT_DATE_EN, FORMAT_TIME, TYPE } from '@/utils/constants/AppConstants';
import { ADD_TASK_OF_PROJECT_URL, VIEW_TASK_OF_PROJECT_URL } from '@/utils/constants/RouteContants';
import { DataViewer } from '@/utils/helpers/common';
import { findIndexByKey, reorderDraggableArray } from '@/utils/helpers/globalHelper';
import { updateDisplayOrder } from '@/utils/services/ProjectApiService';

import ChatIcon from '@/assets/icons/Chats.svg';

import { LegacyTaskTable } from './LegacyTaskTable';

interface ITaskTableProps {
  listTask: ITask[];
  fetchData?: () => void;
  processName?: string;
  processId?: string;
  setTasksList?: any;
  tasksList?: any;
}

const TaskTable = ({ listTask, fetchData, processName, processId, setTasksList, tasksList }: ITaskTableProps) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { isExternalRole, isInternalRole } = useAuthorization();
  const { mutate: deleteTaskProject } = useMutation(`/prj/projects/${id}/task`, {
    method: 'DELETE',
    bodyType: 'json',
    showToastError: true,
    showToastSuccess: false
  });
  const onClickChatIcon = (projectId: string, taskId: string) => {
    return navigate(`${VIEW_TASK_OF_PROJECT_URL(projectId ?? '', taskId ?? '')}?${READED}=true`);
  };

  const generateColumns = () => {
    return [
      { key: 'sort', align: 'center', width: 35, render: () => <DragHandle /> },
      {
        key: 'taskType',
        width: 65,
        render: (record: ITask) => {
          return (
            <div className='font-medium task-type-tag'>
              <TagTaskType type={record.taskType} />
            </div>
          );
        }
      },
      {
        key: 'title',
        width: 480,
        render: (record: ITask) => {
          const showNameTask = isExternalRole && record.preStatusName === STATUS_NAME.TODO;
          return showNameTask ? (
            <AppTooltip className='block max-w-full w-fit truncate' title={record?.taskName}>
              <span>{DataViewer.display(record?.taskName)}</span>
            </AppTooltip>
          ) : (
            <AppTooltip className='block max-w-full w-fit truncate' title={record?.taskName}>
              <Link to={`${VIEW_TASK_OF_PROJECT_URL(id ?? '', record.id ?? '')}?${READED}=true`} className='underline font-normal'>
                {record?.taskName}
              </Link>
            </AppTooltip>
          );
        }
      },
      {
        key: 'comment',
        width: 35,
        render: (record: ITask) => {
          const disabledLink = isExternalRole && record.preStatusName === STATUS_NAME.TODO;
          return (
            !disabledLink &&
            record.hasComments && (
              <button
                onClick={() => onClickChatIcon(id ?? '', record.id ?? '')}
                className={classNames(
                  `m-0 p-0 bg-[transparent] cursor-pointer border-0 relative w-[20px] flex items-center justify-center after:absolute after:h-[6px] after:w-[6px] after:bg-negative after:rounded-full after:top-[-2px] after:right-[-2px] after:border-[2px] after:border-white after:border-solid`,
                  record?.hasUnreadComments && `after:content-['']`
                )}
              >
                <ChatIcon />
              </button>
            )
          );
        }
      },

      {
        key: 'deadline',
        width: 170,
        render: (record: ITask) => {
          return (
            <div className='font-normal'>
              {t('project_detail:task_list:deadline')} : {DataViewer.displayTime(record?.deadline, FORMAT_DATE_EN)}
              {record?.deadlineTime ? `\u3000${dayjs(record.deadlineTime, 'HH:mm').format(FORMAT_TIME)}` : ''}
            </div>
          );
        }
      },
      {
        key: 'pic',
        width: 240,
        render: (record: ITask) => {
          return (
            <AppTooltip title={record.picName}>
              <div className='font-normal min-w-0 truncate'>
                {t('project_detail:task_list:task_pic')} : {DataViewer.display(record.picName)}
              </div>
            </AppTooltip>
          );
        }
      },
      {
        key: 'status',
        width: 144,
        render: (record: ITask) => {
          const status = isPending(record) ? STATUS_NAME.DELAYED : record?.statusName;
          return renderTaskStatus(status, 'ml-auto');
        }
      },
      {
        key: 'completeDate',
        width: 140,
        render: (record: ITask) => {
          return (
            <div className='font-normal'>
              {t('project_detail:task_list:completed_date_actual')} : {DataViewer.displayTime(record?.completeDate, FORMAT_DATE_EN)}
            </div>
          );
        }
      }
    ];
  };

  const listDataTask = useMemo(() => {
    return listTask.map((item: ITask) => ({
      ...item,
      key: item.id
    }));
  }, [listTask]);

  const handleDeleteRow = async (keys: React.Key[], records: any[] | undefined) => {
    if (!records) return;
    const idsToDelete = records.map((item) => ({
      id: item.id || '',
      version: item.version || ''
    }));

    if (!idsToDelete.length) {
      return 0;
    }

    const showPopup = await showBasePopup({
      type: 'discard',
      title: t('common:MSG_C_012:title', { field: t('project_detail:task_list:task') }) ?? '',
      msg: t('common:MSG_C_012:description', { field: t('project_detail:task_list:task') }) ?? ''
    });

    if (showPopup === 'confirm') {
      const result = await deleteTaskProject({
        tasks: idsToDelete
      });
      if (!result) return 0;
      const { data } = result;

      if (data.successCount === keys.length) {
        dispatch(
          setAlertNotification({
            show: true,
            type: TYPE.SUCCESS,
            message: t('common:MSG_C_003', { item: t('master_data:phrase:delete') })
          })
        );
      }

      if (data.successCount < keys.length) {
        dispatch(
          setAlertNotification({
            show: true,
            type: TYPE.SUCCESS,
            message: t('common:MSG_C_020', { deletable: data.successCount, total: keys.length })
          })
        );
      }
      fetchData && fetchData();
      return data.successCount;
    }
  };

  const getEmptyDataAlert = (): React.ReactNode => {
    return <EmptyTextData className='text-center' text={'template_layout:emty_table'} />;
  };

  const handleRowCheck = (selectedRowKeys: React.Key[], selectedRows: ITask[]) => {
    return {
      selectedRowKeys,
      selectedRows
    };
  };

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = findIndexByKey(listDataTask, active?.id as string);
      const overIndex = findIndexByKey(listDataTask, over?.id as string);
      const obj: any = listDataTask.find((record) => record.key === active?.id);
      const newDocuments = (tasksList ?? []).map((item: any) => {
        let projectTasks = item.projectTasks;
        if (item.id === obj.projectProcessId) {
          projectTasks = reorderDraggableArray(projectTasks, activeIndex, overIndex);
        }
        return {
          ...item,
          projectTasks
        };
      });
      setTasksList(newDocuments);

      const data = newDocuments.find((item: any) => item.id === obj.projectProcessId);
      try {
        await updateDisplayOrder(obj?.projectProcessId, {
          projectTasks: data?.projectTasks?.map((item: { id: any }, displayOrder: any) => ({
            projectTaskId: item.id,
            displayOrder
          }))
        });
      } catch {}
    }
  };

  return (
    <div className='table-items pl-[40px]'>
      <div className='table-items-collapse'></div>
      <div className='flex-1 table-items-table'>
        <div className='flex items-center justify-start mb-3 ml-[13px]'>
          {isInternalRole && (
            <BaseButton
              type='primary'
              size='medium'
              onClick={() =>
                navigate(ADD_TASK_OF_PROJECT_URL(id ?? ''), {
                  state: { processName, processId }
                })
              }
            >
              <span>{t('project_detail:buttons:add_task')}</span>
            </BaseButton>
          )}
        </div>
        {listDataTask.length ? (
          <LegacyTaskTable
            tableLayout='fixed'
            className='base-table-bordered'
            columns={generateColumns()}
            dataSource={listDataTask}
            onSelected={handleRowCheck}
            rowClassName={(record: ITask) => `bg-blue3 ${isPending(record) ? 'row-delay' : ''}`}
            pagination={false}
            isShowDelete
            disabledSelectionRows={
              isExternalRole
                ? []
                : (listDataTask.filter((record) => record.preStatusName !== STATUS_NAME.TODO)?.map((item) => item.id) as unknown as number[])
            }
            showHeader={false}
            handleDeleteClick={handleDeleteRow}
            onRowAble={true}
            onDragEnd={onDragEnd}
          />
        ) : (
          getEmptyDataAlert()
        )}
      </div>
    </div>
  );
};

export default TaskTable;
